// Pure interior undo/redo snapshot helpers extracted 1:1 from RoomCanvas.tsx.
// No PIXI, no React, no component state.
// A snapshot captures only the layout-affecting fields of every furniture item,
// sorted by id so two snapshots of the same layout serialize identically.
import type { HouseFurniture } from '../../../models/apiTypes';
import type { InteriorSnapshot } from '../../../stores/useInteriorHistoryStore';
import { toPlacementMode } from '../interaction/placement';
import type { PreviewItem } from '../../../stores/useFurniturePreviewStore';

/**
 * Snapshot id of a try-on (preview) item: negative so it can never collide with a
 * real furniture id. Previews are part of the undo history too — moving or
 * flipping something you are trying on is an editor action like any other.
 */
export const previewSnapshotId = (instanceId: number): number => -instanceId;
export const isPreviewSnapshotId = (id: number): boolean => id < 0;

export const toSnapshot = (items: HouseFurniture[], previews: PreviewItem[] = []): InteriorSnapshot =>
  [
    ...items.map((f) => ({
      id: f.id,
      is_placed: f.is_placed,
      x: f.x ?? null,
      y: f.y ?? null,
      is_mirrored: f.is_mirrored,
      placement_mode: toPlacementMode(f.placement_mode),
      rug_z_order: f.rug_z_order ?? 0,
      wall_floor_offset: f.wall_floor_offset ?? 0,
      room_index: f.room_index,
    })),
    ...previews.map((p) => ({
      id: previewSnapshotId(p.instanceId),
      is_placed: true,
      x: p.x,
      y: p.y,
      is_mirrored: p.isMirrored,
      placement_mode: toPlacementMode(p.placement_mode),
      rug_z_order: 0,
      wall_floor_offset: 0,
      room_index: 0,
    })),
  ].sort((a, b) => a.id - b.id);

/** The try-on items that belong to this room (the preview store is per house). */
export const roomPreviews = (
  state: { previewMapObjectId: number | null; previewItems: PreviewItem[] },
  mapObjectId: number,
): PreviewItem[] => (state.previewMapObjectId === mapObjectId ? state.previewItems : []);

export const snapshotSignature = (snap: InteriorSnapshot): string => JSON.stringify(snap);
